import React from "react"
import { Dimensions, useColorScheme } from "react-native"

import { logger } from "@/base/logger"
import AsyncStorage from "@react-native-async-storage/async-storage"

import { getBreakpointForWidth } from "./theme"

const COLOR_MODE_STORAGE_KEY = "@colorMode"

export type ColorModeValue = "dark" | "light"

/**
 * Logic for managing the color mode
 */
function useAppThemeProvider() {
  const preference = useColorScheme()

  const [screenSize, setScreenSize] = React.useState(
    getBreakpointForWidth(Dimensions.get("window").width),
  )
  // const [breakpointKey, setBreakpointKey] = React.useState()
  React.useEffect(() => {
    const subscription = Dimensions.addEventListener("change", () => {
      const nextSize = getBreakpointForWidth(Dimensions.get("window").width)
      if (nextSize !== screenSize) {
        setScreenSize(nextSize)
      }
    })

    return () => {
      subscription.remove()
    }
  }, [screenSize])
  const [currentColorMode, setCurrentColorMode] =
    React.useState<ColorModeValue>(preference ?? "light")

  /**
   * On load, check for saved color mode and set it if it exists
   */
  React.useEffect(() => {
    AsyncStorage.getItem(COLOR_MODE_STORAGE_KEY)
      .then((savedValue) => {
        if (savedValue === "light" || savedValue === "dark") {
          return setCurrentColorMode(savedValue)
        }
        return undefined
      })
      .catch((error) => {
        logger.error(error)
      })
  }, [])

  /**
   * Update the color mode and save it to storage
   */
  const onColorModeChanged = React.useCallback((nextValue: string) => {
    if (nextValue === "light" || nextValue === "dark") {
      setCurrentColorMode(nextValue)
      AsyncStorage.setItem(COLOR_MODE_STORAGE_KEY, nextValue).catch(
        (error) => {
          logger.error(error)
        },
      )
    } else {
      throw new TypeError(`Invalid color mode: ${nextValue}`)
    }
  }, [])
  return {
    colorMode: currentColorMode,
    onColorModeChanged,
    screenSize,
  }
}

type ContextValue = ReturnType<typeof useAppThemeProvider>

const AppThemeContext = React.createContext<ContextValue | undefined>(
  undefined,
)

export function AppThemeContextProvider({
  children,
}: React.PropsWithChildren): React.JSX.Element | null {
  const value = useAppThemeProvider()
  return (
    <AppThemeContext.Provider value={value}>
      {children}
    </AppThemeContext.Provider>
  )
}

export function useAppThemeContext() {
  const ctx = React.useContext(AppThemeContext)
  if (typeof ctx === "undefined") {
    throw new TypeError(
      `ThemeProvider Context must be used inside of provider`,
    )
  }
  return ctx
}
export function useCurrentColorMode() {
  return useAppThemeContext().colorMode
}

export function useIsDarkMode() {
  return useCurrentColorMode() === "dark"
}

export function useScreenSizeBreakpoint() {
  return useAppThemeContext().screenSize
}
